/**
 * Cart-counter binding for the masthead cart icon.
 *
 * Markup contract: any element carrying `data-amri-cart-count` is treated as
 * a cart-count badge. The `_LoginActionBar.cshtml` + `_MobileLoginActionBar.cshtml`
 * partials render the desktop + mobile instances; both share the same hook so
 * one fetch updates both badges.
 *
 *   <a class="icon-btn amri-count-badge" data-count="0" data-amri-cart-count …>
 *
 * Behavior on DOMContentLoaded:
 *   1. GET `/umbraco/surface/checkoutsurface/getcartitemscount`
 *   2. Parse `{ count: number }` from JSON
 *   3. For each `[data-amri-cart-count]`:
 *        - set `data-count` attr (CSS uses this for the badge bubble)
 *        - update `aria-label` for screen readers
 *
 * Failures (network error, non-200, non-numeric payload) leave the
 * server-rendered `0` baseline in place — the cart icon still renders, just
 * without a live count. We don't surface user-facing errors for a non-critical
 * widget.
 */

const ENDPOINT = '/umbraco/surface/checkoutsurface/getcartitemscount';
const HOOK = '[data-amri-cart-count]';

interface CartCountResponse {
    count: number;
}

function applyCount(count: number): void {
    const nodes = document.querySelectorAll<HTMLElement>(HOOK);
    nodes.forEach(node => {
        node.setAttribute('data-count', String(count));
        const noun = count === 1 ? 'item' : 'items';
        node.setAttribute('aria-label', `Shopping cart (${count} ${noun})`);
    });
}

export async function refreshCartCount(): Promise<void> {
    try {
        const response = await fetch(ENDPOINT, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
        });
        if (!response.ok) {
            return;
        }
        const payload = (await response.json()) as CartCountResponse;
        if (typeof payload?.count === 'number' && Number.isFinite(payload.count)) {
            applyCount(payload.count);
        }
    } catch {
        // Cart counter is non-critical chrome; never surface fetch errors.
    }
}

export function initCartCounter(): void {
    if (document.querySelector(HOOK) === null) {
        return;
    }
    void refreshCartCount();
}
